import { z } from 'zod';

/**
 * Generic function to validate data against a Zod schema
 * @param data - The data to validate
 * @param schema - The Zod schema to validate against
 * @throws Error if validation fails
 * @returns The parsed and validated data
 */
export const validateResponseToSchema = <T>(data: unknown, schema: z.ZodSchema<T>): T => {
  const result = schema.safeParse(data);
  if (!result.success) {
    // Log the validation failure details for easier debugging
    console.error('Zod validation failed:', {
      error: result.error,
      receivedData: JSON.stringify(data, null, 2),
    });
    throw new Error('Zod parsing error');
  }
  return result.data;
};

export type SafeParseResult<T> = { success: true; data: T } | { success: false; issues: string[] };

export const safeParseToSchema = <T>(data: unknown, schema: z.ZodSchema<T>): SafeParseResult<T> => {
  const result = schema.safeParse(data);
  if (result.success) return { success: true, data: result.data };

  const issues = result.error.issues.map((i) => `${i.path.join('.') || '(root)'}: ${i.message}`);
  console.warn('Zod parse failed:', { issues, receivedData: JSON.stringify(data) });
  return { success: false, issues };
};
